// src/utils/userPermissions.js

function normalizeStatus(value) {
  if (value === null || typeof value === "undefined") return "";

  if (typeof value === "object" && value !== null) {
    return String(value.status || "").trim().toLowerCase();
  }

  return String(value).trim().toLowerCase();
}

/**
 * =========================
 * Permisos globales por rol
 * =========================
 *
 * Estos helpers sirven para navegación general, visibilidad de secciones
 * y decisiones que dependen del usuario autenticado.
 *
 * Para una requisición concreta cargada desde backend, NO tomes estos helpers
 * como autoridad final: usa los flags del payload de la requisición.
 */

export function getUserAccess(user) {
  const safeUser = user ?? {};

  const isAdminRole = !!safeUser.is_admin_role;
  const isSuperusuario = !!safeUser.is_superusuario;
  const isSuperusuarioPlus = !!safeUser.is_superusuarioplus;
  const isSuperusuarioPlusUltra = !!safeUser.is_superusuarioplusultra;

  const canAccessAdmin = !!(safeUser.can_access_admin || isAdminRole);
  const canAccessSuperusuario = !!(safeUser.can_access_superusuario || isSuperusuario || isSuperusuarioPlus || isSuperusuarioPlusUltra);
  const canAccessSuperPlus = !!(safeUser.can_access_superplus || isSuperusuarioPlus || isSuperusuarioPlusUltra);
  const canAccessSuperPlusUltra = !!(safeUser.can_access_superplusultra || isSuperusuarioPlusUltra);

  const isPlusOrUltra = isSuperusuarioPlus || isSuperusuarioPlusUltra;
  const isAdminLike =
    isAdminRole || isSuperusuario || isSuperusuarioPlus || isSuperusuarioPlusUltra;

  return {
    isAdminRole,
    isSuperusuario,
    isSuperusuarioPlus,
    isSuperusuarioPlusUltra,

    canAccessAdmin,
    canAccessSuperusuario,
    canAccessSuperPlus,
    canAccessSuperPlusUltra,

    isPlusOrUltra,
    isAdminLike,

    // aliases existentes
    canManageUsers: canAccessAdmin,
    canViewAdminSection: canAccessAdmin,
    canViewSuperusuarioSection: canAccessSuperusuario,
    canViewSuperPlusSection: canAccessSuperPlus,
    canViewSuperPlusUltraSection: canAccessSuperPlusUltra,

    // aliases útiles para requisiciones (a nivel rol/global)
    canOpenRequisitionEditScreen:
      isAdminRole || isSuperusuario || isSuperusuarioPlus || isSuperusuarioPlusUltra,
    canManageRequisitionRealAmounts:
      isSuperusuario || isSuperusuarioPlus || isSuperusuarioPlusUltra,
    canEditRequisitionHeader:
      isAdminRole || isSuperusuario || isSuperusuarioPlus || isSuperusuarioPlusUltra,
    canChangeRequisitionStatus: isPlusOrUltra,
    canInlineChangeRequisitionStatus: isPlusOrUltra,
    isReadOnlyHeaderEditor: false,
  };
}

export function hasAdminAccess(user) {
  return getUserAccess(user).canAccessAdmin;
}

export function hasSuperusuarioAccess(user) {
  return getUserAccess(user).canAccessSuperusuario;
}

export function hasSuperPlusAccess(user) {
  return getUserAccess(user).canAccessSuperPlus;
}

export function hasSuperPlusUltraAccess(user) {
  return getUserAccess(user).canAccessSuperPlusUltra;
}

/**
 * Puede abrir la pantalla de edición de requisiciones.
 * Regla:
 * - admin, superusuario, plus, ultra => sí
 * - usuario => no
 */
export function canOpenRequisitionEditScreen(user) {
  return getUserAccess(user).canOpenRequisitionEditScreen;
}

/**
 * Elegibilidad general por rol para editar requisiciones.
 *
 * Ojo:
 * - NO valida flags del backend
 * - NO debe usarse como autoridad final sobre una requisición cargada
 * - sirve para navegación general / visibilidad preliminar
 */
export function canPotentiallyEditRequisition(user) {
  const access = getUserAccess(user);
  return access.isAdminRole || access.isSuperusuario || access.isSuperusuarioPlus || access.isSuperusuarioPlusUltra;
}

/**
 * Validación estricta por rol + status.
 *
 * Regla de negocio:
 * - superusuarioplus / superusuarioplusultra => sí siempre
 * - superusuario => sí siempre
 * - admin => solo si status === "draft" o "registered"
 * - usuario => no
 *
 * Nota:
 * - si no mandas requisitionOrStatus:
 *   - plus/ultra => true
 *   - superusuario => false, porque no se puede confirmar el status real
 */
export function canEditRequisitionByRoleAndStatus(user, requisitionOrStatus = null) {
  const access = getUserAccess(user);

  if (access.isSuperusuarioPlus || access.isSuperusuarioPlusUltra) {
    return true;
  }

  if (access.isSuperusuario) {
    return true;
  }

  if (!access.isAdminRole) {
    return false;
  }

  if (requisitionOrStatus === null || typeof requisitionOrStatus === "undefined") {
    return false;
  }

  const normalized = normalizeStatus(requisitionOrStatus);
  return normalized === "draft" || normalized === "registered";
}

/**
 * Wrapper compatible con usos anteriores.
 *
 * Comportamiento:
 * - si NO mandas requisitionOrStatus => regresa elegibilidad general por rol
 * - si SÍ mandas requisitionOrStatus => aplica validación estricta
 *
 * Para decisiones reales sobre una requisición cargada desde backend,
 * prefiere getRequisitionCapabilities(...) y relacionados.
 */
export function canEditRequisition(user, requisitionOrStatus = null) {
  if (requisitionOrStatus === null || typeof requisitionOrStatus === "undefined") {
    return canPotentiallyEditRequisition(user);
  }

  return canEditRequisitionByRoleAndStatus(user, requisitionOrStatus);
}

/**
 * Puede editar campos del encabezado (Paso 1).
 * - admin, superusuario, plus, ultra => sí
 * - usuario => no
 */
export function canEditRequisitionHeader(user) {
  return getUserAccess(user).canEditRequisitionHeader;
}

/**
 * Puede cambiar estatus de requisición.
 * - plus / ultra => sí
 * - superusuario / admin / usuario => no
 */
export function canChangeRequisitionStatus(user) {
  return getUserAccess(user).canChangeRequisitionStatus;
}

/**
 * Alias expresivo para el mismo permiso de cambio de estatus.
 */
export function canInlineChangeRequisitionStatus(user) {
  return getUserAccess(user).canInlineChangeRequisitionStatus;
}

/**
 * Perfil que puede editar requisición pero con encabezado readonly.
 * - actualmente ningún perfil entra con encabezado de solo lectura por rol.
 */
export function isReadOnlyHeaderEditor(user) {
  return getUserAccess(user).isReadOnlyHeaderEditor;
}

/**
 * Puede capturar montos reales.
 * - superusuario o superior => sí
 */
export function canManageRequisitionRealAmounts(user) {
  return getUserAccess(user).canManageRequisitionRealAmounts;
}

/**
 * Puede editar partidas de requisición.
 * Requiere una requisición o status concreto para validación estricta.
 */
export function canEditRequisitionItems(user, requisitionOrStatus = null) {
  return canEditRequisitionByRoleAndStatus(user, requisitionOrStatus);
}

/**
 * Puede subir/eliminar cotizaciones.
 * Requiere una requisición o status concreto para validación estricta.
 */
export function canManageRequisitionQuotes(user, requisitionOrStatus = null) {
  return canEditRequisitionByRoleAndStatus(user, requisitionOrStatus);
}

/**
 * ==========================================
 * Helpers para requisiciones cargadas backend
 * ==========================================
 *
 * Estos helpers sí leen los flags reales del payload:
 * - can_edit_requisition
 * - can_edit_requisition_header
 * - can_change_requisition_status
 * - can_manage_real_amounts
 *
 * Son la mejor fuente para decisiones finales en pantalla.
 */

export function getRequisitionCapabilities(requisition) {
  const safe = requisition ?? {};

  return {
    canEditRequisition: !!safe.can_edit_requisition,
    canEditRequisitionHeader: !!safe.can_edit_requisition_header,
    canEditRequestingDepartment: !!safe.can_edit_requesting_department,
    canChangeRequisitionStatus: !!safe.can_change_requisition_status,
    canManageRealAmounts: !!safe.can_manage_real_amounts,
  };
}

export function canEditLoadedRequisition(requisition) {
  return getRequisitionCapabilities(requisition).canEditRequisition;
}

export function canEditLoadedRequisitionHeader(requisition) {
  return getRequisitionCapabilities(requisition).canEditRequisitionHeader;
}

export function canLoadedRequisitionChangeStatus(requisition) {
  return getRequisitionCapabilities(requisition).canChangeRequisitionStatus;
}

export function canManageLoadedRequisitionRealAmounts(requisition) {
  return getRequisitionCapabilities(requisition).canManageRealAmounts;
}

export function hasAnyRequisitionEditSurface(requisition) {
  const caps = getRequisitionCapabilities(requisition);

  return (
    caps.canEditRequisition ||
    caps.canEditRequisitionHeader ||
    caps.canChangeRequisitionStatus ||
    caps.canManageRealAmounts
  );
}

export function isRequisitionEditHardDenied(requisition) {
  return !hasAnyRequisitionEditSurface(requisition);
}

/**
 * Razón de solo lectura / bloqueo visual.
 *
 * Valores:
 * - ""              => editable
 * - "status_locked" => visible pero bloqueada por status
 * - "read_only"     => visible en modo solo lectura por permisos
 */
export function getRequisitionReadOnlyReason(requisition) {
  const safe = requisition ?? {};
  const caps = getRequisitionCapabilities(safe);

  if (caps.canEditRequisition) return "";

  const rawStatus = normalizeStatus(safe?.status);
  if (rawStatus && rawStatus !== "registered") {
    return "status_locked";
  }

  return "read_only";
}

export function isLoadedRequisitionReadOnlyHeader(requisition) {
  const caps = getRequisitionCapabilities(requisition);
  return caps.canEditRequisition && !caps.canEditRequisitionHeader;
}
